<script setup lang="ts">
import { nextTick, ref, watch } from 'vue';
import { confirmState, settleConfirm } from '@/lib/confirm';

/**
 * `<dialog>` natif en `showModal()` : piège le focus, rend le reste inerte et
 * gère Échap sans code maison. Le focus part sur « Annuler » : sur une action
 * irréversible, la touche Entrée ne doit pas confirmer par réflexe.
 */
const dialog = ref<HTMLDialogElement | null>(null);
const cancelButton = ref<HTMLButtonElement | null>(null);

watch(
  () => confirmState.open,
  async (open) => {
    await nextTick();
    if (!dialog.value) return;
    if (open && !dialog.value.open) {
      dialog.value.showModal();
      cancelButton.value?.focus();
    } else if (!open && dialog.value.open) {
      dialog.value.close();
    }
  },
);

/** Clic sur le voile (hors du panneau) = annulation. */
function onBackdropClick(event: MouseEvent): void {
  if (event.target === dialog.value) settleConfirm(false);
}
</script>

<template>
  <dialog
    ref="dialog"
    class="w-[calc(100%-2rem)] max-w-md rounded-xl border border-slate-200 bg-surface p-0 text-ink shadow-xl backdrop:bg-black/50"
    aria-labelledby="confirm-title"
    aria-describedby="confirm-message"
    @cancel.prevent="settleConfirm(false)"
    @click="onBackdropClick"
  >
    <div class="space-y-4 p-6">
      <h2 id="confirm-title" class="text-lg font-semibold text-ink">{{ confirmState.options.title }}</h2>
      <p id="confirm-message" class="whitespace-pre-line text-sm text-ink-soft">{{ confirmState.options.message }}</p>
      <div class="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <button ref="cancelButton" type="button" class="btn-ghost" @click="settleConfirm(false)">Annuler</button>
        <button
          type="button"
          :class="confirmState.options.danger ? 'btn bg-red-600 text-white hover:bg-red-700' : 'btn-primary'"
          @click="settleConfirm(true)"
        >
          {{ confirmState.options.confirmLabel ?? 'Confirmer' }}
        </button>
      </div>
    </div>
  </dialog>
</template>
